import { Platform } from '@angular/cdk/platform';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OnboardingConfig, OnboardingService } from '@delon/abc/onboarding';
import { SettingsService } from '@delon/theme';
import introJs from 'intro.js';
import { Options } from 'intro.js/src/option';
import { NzModalService } from 'ng-zorro-antd/modal';
import BasicIntro from 'src/assets/intro/basic.json';
import DocListActionsIntro from 'src/assets/intro/doc-list-actions.json';

import { ResizeService } from './resize.service';
import { AppPromoComponent } from '../components/promo/promo.component';
import { TourStartComponent } from '../components/tour-start/tour-start.component';

@Injectable({
  providedIn: 'root'
})
export class IntroService {
  introData: { [key: string]: OnboardingConfig } = {};
  introConfig = [
    {
      key: 'dashboard',
      data: BasicIntro,
      paths: ['/dashboard']
    },
    {
      key: 'docList',
      data: DocListActionsIntro,
      paths: [
        '/gl/jv/list',
        '/gl/rv/list',
        '/gl/pv/list',
        '/ar/inv/list',
        '/ar/dn/list',
        '/ar/cn/list',
        '/ar/or/list',
        '/ap/bil/list',
        '/ap/dn/list',
        '/ap/cn/list',
        '/ap/pay-bil/list',
        '/sale/qt/list',
        '/sale/so/list',
        '/sale/do/list',
        '/sale/dn/list',
        '/sale/cn/list',
        '/sale/cs/list',
        '/sale/csr/list',
        '/sale/inv/list',
        '/purchase/pr/list',
        '/purchase/po/list',
        '/purchase/grn/list',
        '/purchase/dn/list',
        '/purchase/cn/list',
        '/purchase/cp/list',
        '/purchase/cpr/list',
        '/purchase/inv/list',
        '/stk/adj/list',
        '/stk/ob/list'
      ]
    }
  ];

  introKeys: { [key: string]: string } = {
    '/dashboard': 'dashboard',
    '/gl/jv/list': 'docList'
  };

  constructor(
    public onboarding: OnboardingService,
    private router: Router,
    private platform: Platform,
    private modalSrv: NzModalService,
    private settingsSrv: SettingsService,
    private resizeService: ResizeService
  ) {
    this.resizeService.isMobile$.subscribe(isMobile => {
      this.introData = {
        dashboard: {
          items: BasicIntro.items.map(item => ({
            ...item,
            selectors:
              isMobile &&
              (item.selectors === 'header-chat' ||
                item.selectors === 'header-support')
                ? 'header-mobile-dropdown-nav'
                : item.selectors
          }))
        },
        docList: DocListActionsIntro
      };
    });
  }

  playIfNew(key: string, config: OnboardingConfig): void {
    if (this.onboarding.running) return;

    const value = this.getData();
    if (!this.platform.isBrowser || value[key] === '1') {
      this.showPromo();
      return;
    }

    value[key] = '1';

    if (key === 'dashboard') {
      this.showTourModal();
    } else {
      this.run(config);
    }
    this.setData(value);
  }

  replay() {
    const info = this.getIntroConfig();
    if (!info) return;

    if (info.key === 'dashboard') this.showTourModal();
    else this.run(info.data);
  }

  show(key: string) {
    const currentStatus = this.settingsSrv.layout.collapsed;
    this.settingsSrv.setLayout('collapsed', false);
    if (currentStatus) {
      setTimeout(() => {
        this.run(this.introData[key]);
      }, 400);
    } else {
      this.run(this.introData[key]);
    }
  }

  private showPromo() {
    const key = this.getData();

    // Only shows modal if:
    // 1. User doesnt have subscription or product key in any company
    // 2. Current company is not sample company
    // 3. Modal have never been shown before
    // 4. Current user is the owner of the company
    if (
      this.settingsSrv.user['notSubscribed'] &&
      (key['promo'] == '0' || !key['promo']) &&
      !this.settingsSrv.user['company']?.isSample &&
      this.settingsSrv.user['company']?.isOwner
    ) {
      // Ensure that the modal didnt get triggered over and over again
      key['promo'] = '1';
      this.setData(key);

      const ref = this.modalSrv.create({
        nzTitle: undefined,
        nzContent: AppPromoComponent,
        nzWidth: '800px',
        nzWrapClassName: 'promo-card',
        nzOnCancel: () => {
          ref.destroy();
        },
        nzFooter: null
      });
    }
  }

  private showTourModal() {
    const ref = this.modalSrv.create({
      nzTitle: undefined,
      nzContent: TourStartComponent,
      nzWidth: '600px',
      nzOnCancel: () => {
        ref.destroy();
      },
      nzFooter: null
    });
  }

  private getData() {
    return JSON.parse(localStorage.getItem('intro') || '{}') || {};
  }

  private setData(value: any): boolean {
    try {
      localStorage.setItem('intro', JSON.stringify(value));
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  getIntroConfig() {
    let path = this.router.url.substring(this.router.url.lastIndexOf('#') + 1);
    if (path.indexOf('?') >= 0) path = path.substring(0, path.indexOf('?'));
    const config = this.introConfig.find(x => x.paths.indexOf(path) >= 0);
    return config;
  }

  private run(config: OnboardingConfig) {
    this.runIntroJs(config);
  }

  private runIntroJs(config: OnboardingConfig) {
    const options: Partial<Options> = {
      showStepNumbers: true,
      showProgress: true,
      steps: config.items?.map(x => {
        return {
          element: x.selectors,
          title: typeof x.title === 'string' ? x.title : '',
          intro: typeof x.content === 'string' ? x.content : ''
        };
      })
    };
    introJs().setOptions(options).start();
  }
}
